import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, MessageSquare, FileText, Check, X, Send, RefreshCw,
  ChevronRight, ChevronDown, AlertTriangle, ThumbsUp, ThumbsDown,
  Edit3, Clock, CheckCircle, XCircle, Loader2, ArrowRight, Scale,
  Copy, Download, Zap, MessageCircle, User, Building2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import { toast } from 'sonner';

const ClauseNegotiator = () => {
  const { user } = useAuth();
  
  // State for creating new session
  const [showCreate, setShowCreate] = useState(true);
  const [contractId, setContractId] = useState('');
  const [partyAName, setPartyAName] = useState('');
  const [partyAEmail, setPartyAEmail] = useState('');
  const [partyBName, setPartyBName] = useState('');
  const [partyBEmail, setPartyBEmail] = useState('');
  const [partyAVersion, setPartyAVersion] = useState('');
  const [partyBVersion, setPartyBVersion] = useState('');
  
  // Active session state
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeConflict, setActiveConflict] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [counterText, setCounterText] = useState('');
  const [viewingParty, setViewingParty] = useState('a');
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (user) {
      setPartyAName(user.username || user.email?.split('@')[0] || '');
      setPartyAEmail(user.email || '');
    }
  }, [user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session?.messages]);

  const createSession = async () => {
    if (!partyAVersion || !partyBVersion) {
      toast.error('Ingresa ambas versiones del contrato');
      return;
    }
    if (!partyBName || !partyBEmail) {
      toast.error('Ingresa los datos de la contraparte');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/negotiation/create', {
        contract_id: contractId || `neg-${Date.now()}`,
        party_a_name: partyAName,
        party_a_email: partyAEmail,
        party_b_name: partyBName,
        party_b_email: partyBEmail,
        party_a_version: partyAVersion,
        party_b_version: partyBVersion
      });
      setSession(data);
      setShowCreate(false);
      toast.success(`Sesión creada: ${data.session_id}`);
      
      if (data.conflicts?.length > 0) {
        setActiveConflict(data.conflicts[0]);
      }
    } catch (error) {
      toast.error('Error al crear sesión de negociación');
    }
    setLoading(false);
  };

  const loadSession = async (sessionId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/negotiation/${sessionId}`);
      setSession(data);
      setShowCreate(false);
      if (data.conflicts?.length > 0) {
        setActiveConflict(data.conflicts[0]);
      }
    } catch (error) {
      toast.error('Sesión no encontrada');
    }
    setLoading(false);
  };

  const respondToProposal = async (proposalId, response) => {
    if (!session) return;
    
    setLoading(true);
    try {
      const { data } = await api.post(`/negotiation/${session.session_id}/respond`, {
        proposal_id: proposalId,
        party: viewingParty,
        response: response,
        counter_text: response === 'counter' ? counterText : null
      });
      setSession(data);
      setCounterText('');
      
      if (response === 'accept') {
        toast.success('Propuesta aceptada');
      } else if (response === 'reject') {
        toast.info('Propuesta rechazada');
      } else {
        toast.info('Contrapropuesta enviada');
      }
    } catch (error) {
      toast.error('Error al responder');
    }
    setLoading(false);
  };

  const sendMessage = async () => {
    if (!session || !chatMessage.trim()) return;
    
    try {
      await api.post(`/negotiation/${session.session_id}/message`, {
        party: viewingParty,
        message: chatMessage
      });
      
      // Reload session to get updated messages
      const { data } = await api.get(`/negotiation/${session.session_id}`);
      setSession(data);
      setChatMessage('');
    } catch (error) {
      toast.error('Error al enviar mensaje');
    }
  };

  const getConflictSeverityColor = (severity) => {
    const colors = {
      'critical': 'bg-red-500/20 border-red-500/30 text-red-400',
      'high': 'bg-orange-500/20 border-orange-500/30 text-orange-400',
      'medium': 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400',
      'low': 'bg-green-500/20 border-green-500/30 text-green-400'
    };
    return colors[severity] || colors['medium'];
  };

  const getProposalForConflict = (conflictId) => {
    return session?.proposals?.find(p => p.conflict_id === conflictId);
  };

  // Create Session View
  if (showCreate) {
    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-emerald-500/20 rounded-lg">
            <Users className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Negociador de Cláusulas</h3>
            <p className="text-sm text-slate-400">Negocia contratos con IA como mediador</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Party A (Current User) */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <h4 className="text-blue-400 font-medium mb-3 flex items-center space-x-2">
              <User className="w-4 h-4" />
              <span>Tu información (Parte A)</span>
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400">Nombre</label>
                <input
                  type="text"
                  value={partyAName}
                  onChange={(e) => setPartyAName(e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">Email</label>
                <input
                  type="email"
                  value={partyAEmail}
                  onChange={(e) => setPartyAEmail(e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs text-slate-400">Tu versión del contrato</label>
              <textarea
                value={partyAVersion}
                onChange={(e) => setPartyAVersion(e.target.value)}
                placeholder="Pega aquí TU versión del contrato..."
                className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm h-32 resize-none"
              />
            </div>
          </div>

          {/* Party B (Counterparty) */}
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
            <h4 className="text-purple-400 font-medium mb-3 flex items-center space-x-2">
              <Building2 className="w-4 h-4" />
              <span>Contraparte (Parte B)</span>
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400">Nombre</label>
                <input
                  type="text"
                  value={partyBName}
                  onChange={(e) => setPartyBName(e.target.value)}
                  placeholder="Nombre de la contraparte"
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">Email</label>
                <input
                  type="email"
                  value={partyBEmail}
                  onChange={(e) => setPartyBEmail(e.target.value)}
                  placeholder="email@contraparte.com"
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs text-slate-400">Versión de la contraparte</label>
              <textarea
                value={partyBVersion}
                onChange={(e) => setPartyBVersion(e.target.value)}
                placeholder="Pega aquí la versión de la CONTRAPARTE..."
                className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm h-32 resize-none"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={createSession}
              disabled={loading}
              className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
              <span>Iniciar Negociación con IA</span>
            </button>
          </div>

          {/* Load existing session */}
          <div className="pt-4 border-t border-slate-700">
            <p className="text-sm text-slate-400 mb-2">¿Ya tienes una sesión?</p>
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="ID de sesión (ej: abc12345)"
                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    loadSession(e.target.value);
                  }
                }}
              />
              <button
                onClick={() => {
                  const input = document.querySelector('input[placeholder*="ID de sesión"]');
                  if (input.value) loadSession(input.value);
                }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
              >
                Cargar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active Negotiation View
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Scale className="w-6 h-6 text-white" />
            <div>
              <h3 className="text-white font-semibold">Negociación Activa</h3>
              <p className="text-white/80 text-sm">Sesión: {session?.session_id}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              session?.status === 'completed' ? 'bg-green-500/20 text-green-300' :
              session?.status === 'active' ? 'bg-yellow-500/20 text-yellow-300' :
              'bg-slate-500/20 text-slate-300'
            }`}>
              {session?.status === 'completed' ? 'Completada' : 
               session?.status === 'active' ? 'En progreso' : session?.status}
            </span>
            <button
              onClick={() => setShowCreate(true)}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Party Toggle */}
        <div className="flex space-x-2 mt-4">
          <button
            onClick={() => setViewingParty('a')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewingParty === 'a' 
                ? 'bg-white text-emerald-600' 
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            {session?.party_a?.name || 'Parte A'}
          </button>
          <button
            onClick={() => setViewingParty('b')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewingParty === 'b' 
                ? 'bg-white text-emerald-600' 
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            {session?.party_b?.name || 'Parte B'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 divide-x divide-slate-700 min-h-[500px]">
        {/* Conflicts List */}
        <div className="p-4 space-y-2 overflow-y-auto max-h-[500px]">
          <h4 className="text-white font-medium mb-3 flex items-center justify-between">
            <span>Puntos de Conflicto</span>
            <span className="text-xs text-slate-400">{session?.pending_clauses?.length || 0} pendientes</span>
          </h4>
          
          {session?.conflicts?.map((conflict) => {
            const proposal = getProposalForConflict(conflict.id);
            const isAgreed = session?.agreed_clauses?.includes(conflict.id);
            
            return (
              <motion.button
                key={conflict.id}
                onClick={() => setActiveConflict(conflict)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  activeConflict?.id === conflict.id 
                    ? 'bg-emerald-500/20 border-emerald-500/50' 
                    : isAgreed
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-white font-medium capitalize">{conflict.type}</span>
                  {isAgreed ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <span className={`text-xs px-2 py-0.5 rounded ${getConflictSeverityColor(conflict.severity)}`}>
                      {conflict.severity}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 line-clamp-2">
                  {conflict.party_a_text?.substring(0, 60)}...
                </p>
              </motion.button>
            );
          })}

          {session?.conflicts?.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay conflictos</p>
            </div>
          )}
        </div>

        {/* Active Conflict Detail */}
        <div className="p-4 overflow-y-auto max-h-[500px]">
          {activeConflict ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-white font-medium capitalize">{activeConflict.type}</h4>
                <span className={`text-xs px-2 py-1 rounded ${getConflictSeverityColor(activeConflict.severity)}`}>
                  Severidad: {activeConflict.severity}
                </span>
              </div>

              {/* Party A Version */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-xs text-blue-400 mb-1 font-medium">{session?.party_a?.name}</p>
                <p className="text-sm text-slate-300">{activeConflict.party_a_text}</p>
              </div>

              {/* Party B Version */}
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                <p className="text-xs text-purple-400 mb-1 font-medium">{session?.party_b?.name}</p>
                <p className="text-sm text-slate-300">{activeConflict.party_b_text}</p>
              </div>

              {/* AI Proposal */}
              {(() => {
                const proposal = getProposalForConflict(activeConflict.id);
                if (!proposal) return null;

                const isAgreed = proposal.status === 'agreed';
                const myResponse = viewingParty === 'a' ? proposal.party_a_acceptance : proposal.party_b_acceptance;
                const otherResponse = viewingParty === 'a' ? proposal.party_b_acceptance : proposal.party_a_acceptance;

                return (
                  <div className={`rounded-lg p-4 ${
                    isAgreed ? 'bg-green-500/10 border border-green-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'
                  }`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <Zap className={`w-4 h-4 ${isAgreed ? 'text-green-400' : 'text-emerald-400'}`} />
                      <span className={`text-sm font-medium ${isAgreed ? 'text-green-400' : 'text-emerald-400'}`}>
                        {isAgreed ? 'Propuesta Acordada' : 'Propuesta de IA'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300 mb-3">{proposal.proposed_text}</p>
                    <p className="text-xs text-slate-400 mb-3 italic">{proposal.rationale}</p>

                    {/* Response Status */}
                    <div className="flex items-center space-x-4 mb-3 text-xs">
                      <div className="flex items-center space-x-1">
                        <span className="text-slate-400">{session?.party_a?.name}:</span>
                        {proposal.party_a_acceptance === 'accept' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : proposal.party_a_acceptance === 'reject' ? (
                          <XCircle className="w-4 h-4 text-red-400" />
                        ) : proposal.party_a_acceptance === 'counter' ? (
                          <Edit3 className="w-4 h-4 text-yellow-400" />
                        ) : (
                          <Clock className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-slate-400">{session?.party_b?.name}:</span>
                        {proposal.party_b_acceptance === 'accept' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : proposal.party_b_acceptance === 'reject' ? (
                          <XCircle className="w-4 h-4 text-red-400" />
                        ) : proposal.party_b_acceptance === 'counter' ? (
                          <Edit3 className="w-4 h-4 text-yellow-400" />
                        ) : (
                          <Clock className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {!isAgreed && !myResponse && (
                      <div className="space-y-2">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => respondToProposal(proposal.id, 'accept')}
                            disabled={loading}
                            className="flex-1 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm flex items-center justify-center space-x-1"
                          >
                            <ThumbsUp className="w-4 h-4" />
                            <span>Aceptar</span>
                          </button>
                          <button
                            onClick={() => respondToProposal(proposal.id, 'reject')}
                            disabled={loading}
                            className="flex-1 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm flex items-center justify-center space-x-1"
                          >
                            <ThumbsDown className="w-4 h-4" />
                            <span>Rechazar</span>
                          </button>
                        </div>
                        <div className="space-y-1">
                          <textarea
                            value={counterText}
                            onChange={(e) => setCounterText(e.target.value)}
                            placeholder="O escribe tu contrapropuesta..."
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm resize-none h-16"
                          />
                          <button
                            onClick={() => respondToProposal(proposal.id, 'counter')}
                            disabled={loading || !counterText.trim()}
                            className="w-full py-2 bg-yellow-600 hover:bg-yellow-500 disabled:bg-slate-600 text-white rounded-lg text-sm flex items-center justify-center space-x-1"
                          >
                            <Edit3 className="w-4 h-4" />
                            <span>Enviar Contrapropuesta</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {myResponse && !isAgreed && (
                      <p className="text-xs text-slate-400 text-center">
                        Ya respondiste: <span className="font-medium">{myResponse}</span>. Esperando a la otra parte.
                      </p>
                    )}

                    {/* Alternatives */}
                    {proposal.alternatives?.length > 0 && !isAgreed && (
                      <div className="mt-3 pt-3 border-t border-slate-600">
                        <p className="text-xs text-slate-400 mb-2">Alternativas sugeridas:</p>
                        <div className="flex flex-wrap gap-1">
                          {proposal.alternatives.map((alt, idx) => (
                            <span key={idx} className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded">
                              {alt.text}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400">
              <div className="text-center">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Selecciona un conflicto</p>
              </div>
            </div>
          )}
        </div>

        {/* Chat */}
        <div className="flex flex-col">
          <div className="p-3 border-b border-slate-700">
            <h4 className="text-white font-medium flex items-center space-x-2">
              <MessageSquare className="w-4 h-4 text-slate-400" />
              <span>Chat de Negociación</span>
            </h4>
          </div>
          
          <div className="flex-1 overflow-y-auto p-3 space-y-2 max-h-[380px]">
            {session?.messages?.length === 0 && (
              <div className="text-center py-8 text-slate-400 text-sm">
                <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Sin mensajes aún</p>
              </div>
            )}
            
            {session?.messages?.map((msg, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-2 rounded-lg text-sm ${
                  msg.party === viewingParty 
                    ? 'bg-emerald-500/20 ml-4' 
                    : 'bg-slate-700/50 mr-4'
                }`}
              >
                <p className="text-xs text-slate-400 mb-1">
                  {msg.party === 'a' ? session?.party_a?.name : session?.party_b?.name}
                </p>
                <p className="text-slate-200">{msg.message}</p>
              </motion.div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-3 border-t border-slate-700">
            <div className="flex space-x-2">
              <input
                type="text"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Escribe un mensaje..."
                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
              />
              <button
                onClick={sendMessage}
                disabled={!chatMessage.trim()}
                className="p-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 text-white rounded-lg"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="p-4 border-t border-slate-700 bg-slate-800/50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400">Progreso de negociación</span>
          <span className="text-sm text-white font-medium">
            {session?.agreed_clauses?.length || 0} / {session?.conflicts?.length || 0} acordados
          </span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ 
              width: `${session?.conflicts?.length ? (session.agreed_clauses?.length / session.conflicts.length) * 100 : 0}%` 
            }}
          />
        </div>
        
        {session?.status === 'completed' && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={async () => {
                try {
                  const { data } = await api.get(`/negotiation/${session.session_id}/final-contract`);
                  // Copy to clipboard
                  navigator.clipboard.writeText(data.content);
                  toast.success('Contrato final copiado al portapapeles');
                } catch (error) {
                  toast.error('Error al generar contrato final');
                }
              }}
              className="px-6 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center space-x-2"
            >
              <Download className="w-5 h-5" />
              <span>Descargar Contrato Final</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClauseNegotiator;
